/**
 * RoleLanding
 * Resolves the post-login / root ("/") landing route by role, instead of
 * hardcoding "/dashboard" (which requires `advisor:access` and bounces WRISE /
 * admin to /access-denied).
 *
 * - advisor portal users        → /dashboard
 * - case-only roles (WRISE,      → /cases  (CasesRouter self-dispatches per role)
 *   admin)
 * - neither                      → /access-denied
 *
 * The case predicate mirrors the `/cases` route guard
 * (anyPermissions: ['advisor:access', 'client_applications:wrise_comment'])
 * so a user we send to /cases is never bounced by that guard.
 */

import { Navigate } from 'react-router-dom'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuthStore } from '@/stores/authStore'
import { PageSpinner } from '@/components/ui/loading'

export type HomeRoute = '/dashboard' | '/cases' | '/access-denied'

// EXACT-match checks (permissionSet), NOT can(): can()'s manage-wildcard treats
// `advisor.manage` (admin's advisor-registration mgmt) as `advisor:access`,
// which would mis-land admin on the advisor dashboard instead of /cases.
// The landing must key off the literal portal-access marker, not a wildcard.
export function resolveHomeRoute(permissionSet: Set<string>): HomeRoute {
  const hasAdvisorPortal =
    permissionSet.has('advisor:access') || permissionSet.has('advisor.access')
  const isCaseRole =
    permissionSet.has('client_applications:wrise_comment') ||
    permissionSet.has('client_applications.wrise_comment')

  if (hasAdvisorPortal) return '/dashboard'
  if (isCaseRole) return '/cases'
  return '/access-denied'
}

export function RoleLanding() {
  const { permissionSet, isLoading } = usePermissions()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (isLoading) {
    return <PageSpinner fullScreen />
  }

  return <Navigate to={resolveHomeRoute(permissionSet)} replace />
}

export default RoleLanding
